import { ApiService, TApiServiceError, TApiServiceOptions } from "../apiService";
import type {
  TDeleteFileApiResponse,
  TFileApiResponse,
  TFileListApiResponse,
  TSignedUrlResponse,
  TUploadFilePayload,
} from "./files.types";

import { handleAsync } from "@/utils";
import { TApiResponse } from "../../types/api";
import { withAuthHooks } from "../auth/auth.ky.hooks";

const apiService = new ApiService({
  hooks: {
    ...withAuthHooks,
  },
});

class FilesService {
  private rootEndpoint = "files" as const;

  /**
   * Upload a file.
   * @param payload - File upload payload
   * @param options - Optional API options
   * @returns Uploaded file information
   */
  uploadFile(payload: TUploadFilePayload, options?: TApiServiceOptions) {
    const formData = new FormData();
    formData.append("file", payload.file);

    const searchParams = new URLSearchParams();
    searchParams.set("file_type", payload.fileType);
    if (payload.companyId) searchParams.set("company_id", payload.companyId);
    if (payload.contextId) searchParams.set("context_id", payload.contextId);
    if (payload.isPublic !== undefined) searchParams.set("is_public", String(payload.isPublic));
    if (payload.consentRequired !== undefined) searchParams.set("consent_required", String(payload.consentRequired));
    if (payload.retentionDays !== undefined) searchParams.set("retention_days", String(payload.retentionDays));

    const endpoint = `${this.rootEndpoint}/upload?${searchParams.toString()}`;

    return handleAsync<TApiServiceError, TFileApiResponse>(() => apiService.post(endpoint, formData, options));
  }

  /**
   * Download a file.
   * @param fileId - File ID to download
   * @param consentGiven - Whether consent was given
   * @param options - Optional API options
   * @returns File blob
   */
  async downloadFile(fileId: string, consentGiven = false, options?: TApiServiceOptions) {
    const searchParams = new URLSearchParams();
    if (consentGiven) searchParams.set("consent_given", "true");

    const endpoint = `${this.rootEndpoint}/${fileId}/download${searchParams.toString() ? `?${searchParams.toString()}` : ""}`;

    try {
      // getRaw() throws HTTPError on non-2xx, preserving the 401 refresh chain
      const response = await apiService.getRaw(endpoint, options);
      const blob = await response.blob();
      return { data: blob, error: null };
    } catch (error) {
      return { data: null, error: error as TApiServiceError };
    }
  }

  /**
   * Get signed URL for file access.
   * @param fileId - File ID
   * @param expirationSeconds - URL expiration time in seconds (default: 3600)
   * @param method - HTTP method for URL (default: 'GET')
   * @param options - Optional API options
   * @returns Signed URL response
   */
  getSignedUrl(fileId: string, expirationSeconds = 3600, method = "GET", options?: TApiServiceOptions) {
    const searchParams = new URLSearchParams({
      expiration_seconds: String(expirationSeconds),
      method,
    });

    return handleAsync<TApiServiceError, TApiResponse<TSignedUrlResponse>>(() =>
      apiService.get(`${this.rootEndpoint}/${fileId}/url?${searchParams.toString()}`, options)
    );
  }

  /**
   * Serve a file by its storage path.
   * @param filePath - Storage path of the file
   * @param consentGiven - Whether consent was given
   * @param options - Optional API options
   * @returns File blob
   */
  async serveFileByPath(filePath: string, consentGiven = false, options?: TApiServiceOptions) {
    const searchParams = new URLSearchParams();
    if (consentGiven) searchParams.set("consent_given", "true");

    const endpoint = `${this.rootEndpoint}/${filePath}${searchParams.toString() ? `?${searchParams.toString()}` : ""}`;

    try {
      // getRaw() throws HTTPError on non-2xx, preserving the 401 refresh chain
      const response = await apiService.getRaw(endpoint, options);
      const blob = await response.blob();
      return { data: blob, error: null };
    } catch (error) {
      return { data: null, error: error as TApiServiceError };
    }
  }

  /**
   * List files accessible by user.
   * @param options - Optional query parameters and API options
   * @returns List of files with pagination
   */
  listFiles(
    options?: {
      file_type?: string;
      company_id?: string;
      is_public?: boolean;
      page?: number;
      size?: number;
    } & TApiServiceOptions
  ) {
    const { file_type, company_id, is_public, page = 1, size = 20, ...apiOptions } = options ?? {};

    const searchParams = new URLSearchParams();
    if (file_type) searchParams.set("file_type", file_type);
    if (company_id) searchParams.set("company_id", company_id);
    if (is_public !== undefined) searchParams.set("is_public", String(is_public));
    searchParams.set("page", String(page));
    searchParams.set("size", String(size));

    const queryString = searchParams.toString();
    const endpoint = queryString ? `${this.rootEndpoint}/?${queryString}` : `${this.rootEndpoint}/`;

    return handleAsync<TApiServiceError, TFileListApiResponse>(() => apiService.get(endpoint, apiOptions));
  }

  /**
   * Get files for a specific company.
   * @param companyId - Company ID
   * @param options - Optional API options
   * @returns List of files for the company
   */
  getCompanyFiles(companyId: string, options?: TApiServiceOptions) {
    return this.listFiles({
      company_id: companyId,
      ...options,
    });
  }

  /**
   * Delete a file.
   * @param fileId - File ID to delete
   * @param options - Optional API options
   * @returns Deletion result
   */
  deleteFile(fileId: string, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TDeleteFileApiResponse>(() =>
      apiService.delete(`${this.rootEndpoint}/${fileId}`, options)
    );
  }

  /**
   * Get a single file by ID.
   * @param fileId - File ID
   * @param options - Optional API options
   * @returns File details
   */
  getFile(fileId: string, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TFileApiResponse>(() =>
      apiService.get(`${this.rootEndpoint}/${fileId}`, options)
    );
  }
}

export const filesService = new FilesService();
